import { SetStateAction, useState } from "react";
import { AnimalType, StageType } from "../../../../../../graphql/types";
import { BeefFarms } from "../../common";
import {
  InterventionStageProps,
  MasterDataEnumType,
} from "../CommonDataParts/InterventionTypes";
import { Origin } from "../types";
import { useFormContext } from "react-hook-form";
import { Box } from "@material-ui/core";
import StageButtonBox from "../../StageButtonBox_v2";
import { BeefBaseline } from "../../../../models/Baseline/BeefBaseline";
import BeefHousingAndManureDialog from "./BeefHousingAndManureDialog";
import BeefOutputDialog from "./BeefOutputDialog";
import BeefFeedDialog from "./BeefFeedDialog";
import BeefInterventionEmissionMitigationDialog from "./BeefInterventionEmissionMitigationDialog";
import BeefInputGrowingDialog from "./BeefInputGrowingDialog";
import BeefInputBreedingDialog from "./BeefInputBreedingDialog";
import { useGetAllDatabasesCompoundFeeds } from "../../../../../../api";
import featureFlags from "../../../../utils/featureFlags";
import { AnimalSystemEnum } from "../../Baseline/types";
import { getFeedsLoadingPlaceholder } from "../../../../helpers/loadingHelper";

interface BeefInterventionStagesProps
  extends Omit<InterventionStageProps, "manureManagementSystems"> {
  farms: BeefFarms[];
  farmId: string;
  farmName: string;
  productionProcessName: string;
  baseline: BeefBaseline;
  origins: Origin[];
  animalType: AnimalType.Beef;
  masterDataEnums: MasterDataEnumType | null;
  stageType?: StageType;
  calculateCompleteness: (validationFunction: any, prefix: string) => number;
}
const BeefInterventionStages = ({
  farms,
  farmName,
  farmId,
  productionProcessName,
  stageIndex,
  baseline,
  compoundFeeds,
  singleIngredients,
  origins,
  formType,
  stageType = StageType.DairyCow,
  animalType,
  calculateCompleteness,
}: BeefInterventionStagesProps) => {
  const fc = useFormContext();
  const [activeDialog, setActiveDialog] = useState<string | null>("");

  const showDialog = (dialog: SetStateAction<string>) => {
    setActiveDialog(dialog);
  };

  const stagePrefix = `stages.${stageIndex}`;

  const { data: fpCompoundFeeds, isLoading } = useGetAllDatabasesCompoundFeeds(AnimalSystemEnum.BEEF_SYSTEM, featureFlags.FlagCoreConnectAPI);

  const getFeeds = () => {
    if(featureFlags.FlagCoreConnectAPI){
      if (isLoading) return [getFeedsLoadingPlaceholder(baseline?.info.databaseFoundation)];
      return fpCompoundFeeds || [];
    }
    return compoundFeeds;
  }
  return (
    <>
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.id`}
        value={`${baseline?.stages?.[stageIndex]?.id}`}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.name`}
        value={`${baseline?.stages?.[stageIndex]?.name}`}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.type`}
        value={stageType}
      />
      <input
        ref={fc.register()}
        type="hidden"
        name={`${stagePrefix}.index`}
        value={stageIndex}
      />


      {stageType === StageType.Breeding && activeDialog === "input" && (
        <BeefInputBreedingDialog
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          productionProcessName={productionProcessName}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {stageType === StageType.Growing && activeDialog === "input" && (
        <BeefInputGrowingDialog
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          productionProcessName={productionProcessName}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {activeDialog === "feed" && (
        <BeefFeedDialog
          compoundFeeds={getFeeds()}
          singleIngredients={singleIngredients}
          origins={origins}
          formType={formType}
          stageIndex={stageIndex}
          stageType={stageType}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
        />
      )}
      {activeDialog === "housing" && (
        <BeefHousingAndManureDialog
          stageType={stageType}
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          formVisible
        />
      )}
      {activeDialog === "output" && (
        <BeefOutputDialog
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          itemIndex={stageIndex}
          stageType={stageType}
        />
      )}
      {activeDialog === "emissions" && (
        <BeefInterventionEmissionMitigationDialog
          stageType={stageType}
          formType={formType}
          stageIndex={stageIndex}
          baseline={baseline}
          handleCancel={() => setActiveDialog(null)}
          itemIndex={stageIndex}
        />
      )}

      <Box
        style={{ display: "flex", width: "100%" }}
        flexDirection="row"
        justifyContent="space-between"
      >
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.TITLE"
          iconCode="general/check-heart"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages && fc.errors?.stages[stageIndex]?.stageData?.input
          }
          handleOpen={() => showDialog("input")}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.FEED.TITLE"
          iconCode="shapes/cube-02"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages && fc.errors?.stages[stageIndex]?.stageData?.feed
          }
          handleOpen={() => showDialog("feed")}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE"
          iconCode="general/building-06"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.housing
          }
          handleOpen={() => showDialog("housing")}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE"
          iconCode="arrows/arrow-circle-broken-right"
          description="SUSTELL.STAGE.INPUT_DATA"
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.output
          }
          handleOpen={() => showDialog("output")}
        />
        <StageButtonBox
          titleCode="SUSTELL.PROCESS.DIALOG.STAGE.EMISSIONS.TITLE"
          iconCode="maps-travel/globe-04"
          description="SUSTELL.STAGE.INPUT_DATA"
          handleOpen={() => showDialog("emissions")}
          error={
            fc.errors?.stages &&
            fc.errors?.stages[stageIndex]?.stageData?.emissions
          }
        />
      </Box>
    </>
  );
};

export default BeefInterventionStages;
